import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ProductContext } from "../../context/ProductContext";
import { useNavigation } from "../../utils/navigation";
import { useTranslation } from "react-i18next";

const Page = styled.footer`
  width: 100%;
  margin-top: 40px;
  background-color: var(--text-100);
  color: var(--bg-100);
`;

const Wrapper = styled.div`
  width: 90%;
  margin: 0 auto;
  padding: var(--spacing-xxl) 0 var(--spacing-lg) 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 768px) {
    max-width: var(--max-width-container);
  }
`;

const LogoMark = styled.div`
  cursor: pointer;
`;

const BrandName = styled.p`
  margin: var(--spacing-sm) 0 0 0;
  font-size: var(--font-size-large);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const BrandTagline = styled.p`
  margin: var(--spacing-xs) 0 0 0;
  font-size: var(--font-size-small);
  color: var(--bg-300);
  text-align: center;
  max-width: 420px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.12);
  margin: var(--spacing-xl) 0;
`;

const LinksGrid = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  text-align: center;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    text-align: left;
  }
`;

const ColTitle = styled.h3`
  margin: 0 0 var(--spacing-md) 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--bg-300);
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);

  @media (min-width: 768px) {
    align-items: flex-start;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xs) var(--spacing-lg);
  justify-items: center;

  @media (min-width: 768px) {
    justify-items: start;
  }
`;

const LinkItem = styled.span`
  font-size: var(--font-size-base);
  color: var(--bg-100);
  cursor: pointer;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--primary-300);
  }
`;

const ViewAllLink = styled(LinkItem)`
  margin-top: var(--spacing-xs);
  font-weight: 700;
  color: var(--primary-300);
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--bg-100);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--primary-300);

    span {
      text-decoration: underline;
    }
  }
`;

const ContactIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--primary-300);
`;

const WarningStrip = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  margin-top: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.06);
  border-radius: var(--border-radius-base);
  color: var(--bg-300);
  font-size: var(--font-size-small);
  font-weight: 600;
  text-align: center;
`;

const TrustpilotWrap = styled.div`
  width: 100%;
  margin-top: var(--spacing-lg);
  filter: invert(1);
`;

const BottomBar = styled.div`
  width: 100%;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const BottomWrapper = styled.div`
  width: 90%;
  margin: 0 auto;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;

  @media (min-width: 768px) {
    max-width: var(--max-width-container);
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Copyright = styled.p`
  margin: 0;
  font-size: var(--font-size-small);
  color: var(--bg-300);
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);

  a {
    display: flex;
    color: var(--bg-100);
    transition: color var(--transition-fast);
  }
  a:hover {
    color: var(--primary-300);
  }
`;

const CATEGORY_LIMIT = 8;

const Footer = () => {
  const { t } = useTranslation();
  const { categories } = useContext(ProductContext);
  const navigate = useNavigate();
  const {
    goToHome,
    goToCategory,
    goToBrands,
    goToSupport,
    goToPrivacyPolicy,
    goToTermsConditions,
  } = useNavigation();

  return (
    <Page>
      <Wrapper>
        <LogoMark onClick={() => navigate(goToHome())}>
          <svg width="44" height="44" viewBox="0 0 298 298" fill="none">
            <path
              d="M148.707 0C66.7245 0 0 66.7245 0 148.707C0 230.689 66.7245 297.414 148.707 297.414C230.689 297.414 297.414 230.689 297.414 148.707C297.414 66.7245 230.689 0 148.707 0ZM294.707 148.707C294.707 188.275 278.843 224.204 253.18 250.567L244.688 242.074C268.158 217.904 282.669 184.962 282.669 148.707C282.669 112.452 267.878 78.8562 243.988 54.6394L252.48 46.1472C278.563 72.557 294.661 108.766 294.661 148.707H294.707ZM17.3576 148.707C17.3576 76.2899 76.2432 17.4043 148.66 17.4043C221.077 17.4043 279.963 76.2899 279.963 148.707C279.963 221.124 221.077 280.009 148.66 280.009C76.2432 280.009 17.3576 221.124 17.3576 148.707ZM250.52 44.2808L242.028 52.7731C217.858 29.2562 184.915 14.7447 148.614 14.7447C112.312 14.7447 79.416 29.2562 55.2458 52.7731L46.7536 44.2808C73.0701 18.6176 108.999 2.75295 148.567 2.75295C188.135 2.75295 224.11 18.6175 250.427 44.3275L250.52 44.2808ZM44.7939 46.1939L53.2862 54.6862C29.3494 78.9029 14.558 112.172 14.558 148.8C14.558 185.429 29.0693 218.044 52.5862 242.214L44.1406 250.707C18.4307 224.39 2.56627 188.415 2.56627 148.847C2.56627 109.279 18.7107 72.6504 44.7939 46.2872V46.1939ZM46.0539 252.667L54.5461 244.174C78.7629 268.064 111.985 282.856 148.614 282.856C185.242 282.856 218.511 268.064 242.728 244.128L251.22 252.62C224.857 278.703 188.602 294.848 148.66 294.848C108.719 294.848 72.5102 278.703 46.147 252.667"
              fill="var(--primary-200)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M207.722 57.7351C202.622 66.9627 200.922 78.8759 205.416 87.3302C214.54 104.491 230.158 114.314 239.894 131.851C273.15 191.781 209.011 201.72 174.784 209.915C155.428 214.537 143.812 230.506 158.492 258.688C214.436 253.98 258.39 207.07 258.39 149.903C258.39 111.144 238.177 77.1018 207.722 57.7266"
              fill="var(--primary-200)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M191.927 105.506C177.373 95.2564 162.525 82.7355 160.972 63.8935C160.307 55.8558 164.875 48.4915 171.632 43.0459C157.459 40.0907 142.84 39.9776 128.623 42.713C114.406 45.4483 100.873 50.9779 88.8086 58.9812C96.9207 91.2874 127.561 94.6763 155.629 103.143C195.722 115.235 214.322 153.656 158.871 159.818C81.4401 168.423 72.2954 187.545 127.861 256.987C133.447 258.095 139.112 258.763 144.803 258.984C114.222 212.039 131.18 198.9 188.95 180.965C245.166 163.485 224.646 128.565 191.925 105.51"
              fill="var(--accent-100)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M123.936 118.509C100.162 114.213 67.312 112.348 58.2119 97.5595C57.3549 96.166 56.5939 94.7157 55.9342 93.2188C45.5264 110.296 40.0342 129.915 40.0626 149.915C40.0626 197.952 71.1131 238.761 114.236 253.341C95.0693 233.567 63.4812 210.21 71.1047 172.043C76.204 146.547 105.038 150.269 127.675 148.404C160.298 145.722 156.82 124.463 123.927 118.509"
              fill="var(--primary-200)"
            />
          </svg>
        </LogoMark>
        <BrandName>SnusCo</BrandName>
        <BrandTagline>{t("FOOTER.WARNING")}</BrandTagline>

        <Divider />

        <LinksGrid>
          <div>
            <ColTitle>{t("FOOTER.CATEGORIES")}</ColTitle>
            <CategoryGrid>
              {categories &&
                categories.slice(0, CATEGORY_LIMIT).map((category, index) => (
                  <LinkItem
                    key={index}
                    onClick={() => navigate(goToCategory(category.name))}
                  >
                    {category.name}
                  </LinkItem>
                ))}
            </CategoryGrid>
            {categories && categories.length > CATEGORY_LIMIT && (
              <ViewAllLink onClick={() => navigate(goToBrands())}>
                {t("FOOTER.VIEW_ALL")}
              </ViewAllLink>
            )}
          </div>

          <div>
            <ColTitle>{t("FOOTER.SUPPORT")}</ColTitle>
            <LinkList>
              <LinkItem onClick={() => navigate(goToSupport())}>
                {t("FOOTER.SUPPORT")}
              </LinkItem>
              <LinkItem onClick={() => navigate(goToSupport())}>
                {t("FOOTER.FAQS")}
              </LinkItem>
              <LinkItem onClick={() => navigate(goToPrivacyPolicy())}>
                {t("FOOTER.PRIVACY_POLICY")}
              </LinkItem>
              <LinkItem onClick={() => navigate(goToTermsConditions())}>
                {t("FOOTER.TERMS_CONDITIONS")}
              </LinkItem>
            </LinkList>
          </div>

          <div>
            <ColTitle>{t("FOOTER.CONTACT")}</ColTitle>
            <LinkList>
              <ContactLink href="mailto:info@snusco.com">
                <ContactIcon>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 7.00005L10.2 11.65C11.2667 12.45 12.7333 12.45 13.8 11.65L20 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="14"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </ContactIcon>
                <span>info@snusco.com</span>
              </ContactLink>
              <ContactLink
                href="https://www.google.com/maps/search/?api=1&query=Michael-Reš-Gasse+2/46+Wien"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ContactIcon>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5 9.5C5 6.09371 8.00993 3 12 3C15.9901 3 19 6.09371 19 9.5C19 11.6449 17.6877 14.0406 15.9606 16.2611C14.5957 18.016 13.0773 19.5329 12 20.5944C10.9227 19.5329 9.40427 18.016 8.03935 16.2611C6.31229 14.0406 5 11.6449 5 9.5ZM12 1C6.99007 1 3 4.90629 3 9.5C3 12.3551 4.68771 15.2094 6.46065 17.4889C7.99487 19.4615 9.7194 21.1574 10.7973 22.2173C10.9831 22.4001 11.1498 22.564 11.2929 22.7071C11.4804 22.8946 11.7348 23 12 23C12.2652 23 12.5196 22.8946 12.7071 22.7071C12.8502 22.564 13.0169 22.4001 13.2027 22.2174L13.2028 22.2173C14.2806 21.1573 16.0051 19.4615 17.5394 17.4889C19.3123 15.2094 21 12.3551 21 9.5C21 4.90629 17.0099 1 12 1ZM12 12.5C13.3807 12.5 14.5 11.3807 14.5 10C14.5 8.61929 13.3807 7.5 12 7.5C10.6193 7.5 9.5 8.61929 9.5 10C9.5 11.3807 10.6193 12.5 12 12.5Z"
                      fill="currentColor"
                    />
                  </svg>
                </ContactIcon>
                <span>Michael-Reš-Gasse 2/46 Wien</span>
              </ContactLink>
              <ContactLink href="tel:+436677628134">
                <ContactIcon>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 5.5C3 14.0604 9.93959 21 18.5 21C18.8862 21 19.2691 20.9859 19.6483 20.9581C20.0834 20.9262 20.3009 20.9103 20.499 20.7963C20.663 20.7019 20.8185 20.5345 20.9007 20.364C21 20.1582 21 19.9181 21 19.438V16.6207C21 16.2169 21 16.015 20.9335 15.842C20.8749 15.6891 20.7795 15.553 20.6559 15.4456C20.516 15.324 20.3262 15.255 19.9468 15.117L16.74 13.9509C16.2985 13.7904 16.0777 13.7101 15.8683 13.7237C15.6836 13.7357 15.5059 13.7988 15.3549 13.9058C15.1837 14.0271 15.0629 14.2285 14.8212 14.6314L14 16C11.3501 14.7999 9.2019 12.6489 8 10L9.36863 9.17882C9.77145 8.93713 9.97286 8.81628 10.0942 8.64506C10.2012 8.49408 10.2643 8.31637 10.2763 8.1317C10.2899 7.92227 10.2096 7.70153 10.0491 7.26005L8.88299 4.05321C8.745 3.67376 8.67601 3.48403 8.55442 3.3441C8.44701 3.22049 8.31089 3.12515 8.15802 3.06645C7.98496 3 7.78308 3 7.37932 3H4.56201C4.08188 3 3.84181 3 3.63598 3.09925C3.4655 3.18146 3.29814 3.33701 3.2037 3.50103C3.08968 3.69907 3.07375 3.91662 3.04189 4.35173C3.01413 4.73086 3 5.11378 3 5.5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ContactIcon>
                <span>+43 667 7628134</span>
              </ContactLink>
            </LinkList>
          </div>
        </LinksGrid>

        <WarningStrip>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <g clipPath="url(#clip0_1208_12314)">
              <path
                d="M12 2C17.523 2 22 6.477 22 12C22.0021 14.6255 20.9715 17.1464 19.1309 19.0186C17.2902 20.8908 14.7872 21.9641 12.162 22.0066C9.53689 22.0491 7.00034 21.0576 5.1 19.246C3.19966 17.4344 2.08799 14.9482 2.005 12.324L2 12L2.004 11.72C2.152 6.327 6.57 2 12 2ZM12.01 15L11.883 15.007C11.64 15.0359 11.4159 15.153 11.2534 15.336C11.0909 15.519 11.0011 15.7552 11.0011 16C11.0011 16.2448 11.0909 16.481 11.2534 16.664C11.4159 16.847 11.64 16.9641 11.883 16.993L12 17L12.127 16.993C12.37 16.9641 12.5941 16.847 12.7566 16.664C12.9191 16.481 13.0089 16.2448 13.0089 16C13.0089 15.7552 12.9191 15.519 12.7566 15.336C12.5941 15.153 12.37 15.0359 12.127 15.007L12.01 15ZM12 7C11.7551 7.00003 11.5187 7.08996 11.3356 7.25272C11.1526 7.41547 11.0357 7.63975 11.007 7.883L11 8V12L11.007 12.117C11.0359 12.36 11.153 12.5841 11.336 12.7466C11.519 12.9091 11.7552 12.9989 12 12.9989C12.2448 12.9989 12.481 12.9091 12.664 12.7466C12.847 12.5841 12.9641 12.36 12.993 12.117L13 12V8L12.993 7.883C12.9643 7.63975 12.8474 7.41547 12.6644 7.25272C12.4813 7.08996 12.2449 7.00003 12 7Z"
                fill="var(--bg-300)"
              />
            </g>
            <defs>
              <clipPath id="clip0_1208_12314">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
          {t("FOOTER.WARNING")}
        </WarningStrip>

        <TrustpilotWrap
          className="trustpilot-widget"
          data-locale="en-US"
          data-template-id="56278e9abfbbba0bdcd568bc"
          data-businessunit-id="66d39814fee7b0c8732e417b"
          data-style-height="52px"
          data-style-width="100%"
        >
          <a
            href="https://www.trustpilot.com/review/snusco.com"
            target="_blank"
            rel="noopener"
          >
            Trustpilot
          </a>
        </TrustpilotWrap>
      </Wrapper>

      <BottomBar>
        <BottomWrapper>
          <Copyright>{t("FOOTER.COPYRIGHT")}</Copyright>
          <IconRow>
            <a
              href="https://www.instagram.com/snuscogmbh/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("ARIA.INSTAGRAM")}
            >
              <svg width="22px" height="22px" viewBox="0 0 24 24" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18ZM12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
                  fill="currentColor"
                />
                <path
                  d="M18 5C17.4477 5 17 5.44772 17 6C17 6.55228 17.4477 7 18 7C18.5523 7 19 6.55228 19 6C19 5.44772 18.5523 5 18 5Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.65396 4.27606C1 5.55953 1 7.23969 1 10.6V13.4C1 16.7603 1 18.4405 1.65396 19.7239C2.2292 20.8529 3.14708 21.7708 4.27606 22.346C5.55953 23 7.23969 23 10.6 23H13.4C16.7603 23 18.4405 23 19.7239 22.346C20.8529 21.7708 21.7708 20.8529 22.346 19.7239C23 18.4405 23 16.7603 23 13.4V10.6C23 7.23969 23 5.55953 22.346 4.27606C21.7708 3.14708 20.8529 2.2292 19.7239 1.65396C18.4405 1 16.7603 1 13.4 1H10.6C7.23969 1 5.55953 1 4.27606 1.65396C3.14708 2.2292 2.2292 3.14708 1.65396 4.27606ZM13.4 3H10.6C8.88684 3 7.72225 3.00156 6.82208 3.0751C5.94524 3.14674 5.49684 3.27659 5.18404 3.43597C4.43139 3.81947 3.81947 4.43139 3.43597 5.18404C3.27659 5.49684 3.14674 5.94524 3.0751 6.82208C3.00156 7.72225 3 8.88684 3 10.6V13.4C3 15.1132 3.00156 16.2777 3.0751 17.1779C3.14674 18.0548 3.27659 18.5032 3.43597 18.816C3.81947 19.5686 4.43139 20.1805 5.18404 20.564C5.49684 20.7234 5.94524 20.8533 6.82208 20.9249C7.72225 20.9984 8.88684 21 10.6 21H13.4C15.1132 21 16.2777 20.9984 17.1779 20.9249C18.0548 20.8533 18.5032 20.7234 18.816 20.564C19.5686 20.1805 20.1805 19.5686 20.564 18.816C20.7234 18.5032 20.8533 18.0548 20.9249 17.1779C20.9984 16.2777 21 15.1132 21 13.4V10.6C21 8.88684 20.9984 7.72225 20.9249 6.82208C20.8533 5.94524 20.7234 5.49684 20.564 5.18404C20.1805 4.43139 19.5686 3.81947 18.816 3.43597C18.5032 3.27659 18.0548 3.14674 17.1779 3.0751C16.2777 3.00156 15.1132 3 13.4 3Z"
                  fill="currentColor"
                />
              </svg>
            </a>
            <a
              href="https://wa.me/message/PFGFC6WZV66NC1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("ARIA.WHATSAPP")}
            >
              <svg width="22px" height="22px" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6.014 8.00613C6.12827 7.1024 7.30277 5.87414 8.23488 6.01043C9.14051 6.18132 9.85859 7.74261 10.2635 8.44465C10.5504 8.95402 10.3641 9.4701 10.0965 9.68787C9.7355 9.97883 9.17099 10.3803 9.28943 10.7834C9.5 11.5 12 14 13.2296 14.7107C13.695 14.9797 14.0325 14.2702 14.3207 13.9067C14.5301 13.6271 15.0466 13.46 15.5548 13.736C16.3138 14.178 17.0288 14.6917 17.69 15.27C18.0202 15.546 18.0977 15.9539 17.8689 16.385C17.4659 17.1443 16.3003 18.1456 15.4542 17.9421C13.9764 17.5868 8 15.27 6.08033 8.55801C5.97237 8.24048 5.99955 8.12044 6.014 8.00613Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 23C10.7764 23 10.0994 22.8687 9 22.5L6.89443 23.5528C5.56462 24.2177 4 23.2507 4 21.7639V19.5C1.84655 17.492 1 15.1767 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23ZM6 18.6303L5.36395 18.0372C3.69087 16.4772 3 14.7331 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C11.0143 21 10.552 20.911 9.63595 20.6038L8.84847 20.3397L6 21.7639V18.6303Z"
                  fill="currentColor"
                />
              </svg>
            </a>
          </IconRow>
        </BottomWrapper>
      </BottomBar>
    </Page>
  );
};

export default Footer;
